/**
 * Proyecto: MOGOTAX-CONDUCTOR
 * Detalle de FUEC
 */
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Typography,
  Tag,
  Spin,
  Button,
  Descriptions,
  message,
  Alert,
} from "antd";
import {
  LeftOutlined,
  DownloadOutlined,
  FileTextOutlined,
  CarOutlined,
  UserOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import api from "../../../axiosConfig";

const { Title, Text } = Typography;

const FuecDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fuec, setFuec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetchFuec = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/conductor/fuec/${id}`);
        setFuec(response.data.data || response.data);
      } catch (error) {
        console.error("Error al cargar FUEC:", error);
        message.error("Error al cargar el detalle del FUEC");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchFuec();
    }
  }, [id]);

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const response = await api.get(`/conductor/fuec/${id}/pdf`, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `FUEC-${fuec?.numero_fuec || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success("FUEC descargado exitosamente");
    } catch (error) {
      console.error("Error al descargar FUEC:", error);
      message.error("Error al descargar el FUEC");
    } finally {
      setDownloading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-CO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getEstadoColor = (estado) => {
    const colores = {
      activo: "green",
      vigente: "green",
      vencido: "red",
      cancelado: "red",
      pendiente: "orange",
    };
    return colores[estado?.toLowerCase()] || "default";
  };

  const isVigente = () => {
    if (!fuec?.fecha_fin_vigencia) return true;
    const fechaFin = new Date(fuec.fecha_fin_vigencia);
    return fechaFin >= new Date();
  };

  if (loading) {
    return (
      <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "60vh"
      }}>
        <Spin size="large" tip="Cargando FUEC..." />
      </div>
    );
  }

  if (!fuec) {
    return (
      <div style={{ padding: 16 }}>
        <Button
          icon={<LeftOutlined />}
          onClick={() => navigate("/modules/fuecs")}
          style={{ marginBottom: 16 }}
        >
          Volver
        </Button>
        <Alert
          message="FUEC no encontrado"
          description="No se pudo encontrar el FUEC solicitado."
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      {/* Header */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 16,
        flexWrap: "wrap",
        gap: 8,
      }}>
        <Button
          icon={<LeftOutlined />}
          onClick={() => navigate("/modules/fuecs")}
        >
          Volver
        </Button>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleDownloadPdf}
          loading={downloading}
        >
          Descargar PDF
        </Button>
      </div>

      {/* Estado de vigencia */}
      {!isVigente() && (
        <Alert
          message="FUEC Vencido"
          description="Este FUEC ya no está vigente. Por favor contacte a la empresa para renovarlo."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Información Principal */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <FileTextOutlined style={{ fontSize: 32, color: "#1890ff" }} />
          <div>
            <Title level={4} style={{ margin: 0 }}>
              {fuec.numero_fuec || `FUEC #${fuec.id}`}
            </Title>
            <Tag color={getEstadoColor(fuec.estado)} style={{ marginTop: 4 }}>
              {fuec.estado || (isVigente() ? "Vigente" : "Vencido")}
            </Tag>
          </div>
        </div>

        <Descriptions column={1} size="small">
          <Descriptions.Item label={<><CalendarOutlined /> Fecha Inicio</>}>
            {formatDate(fuec.fecha_inicio_vigencia)}
          </Descriptions.Item>
          <Descriptions.Item label={<><CalendarOutlined /> Fecha Fin</>}>
            {formatDate(fuec.fecha_fin_vigencia)}
          </Descriptions.Item>
          {fuec.numero_contrato && (
            <Descriptions.Item label="Contrato">
              {fuec.numero_contrato}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* Vehículo */}
      <Card
        title={<><CarOutlined style={{ marginRight: 8 }} />Vehículo</>}
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Descriptions column={1} size="small">
          <Descriptions.Item label="Placa">
            <Text strong>{fuec.vehiculo?.placa || fuec.vehiculo_placa || "N/A"}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Marca/Modelo">
            {fuec.vehiculo?.marca || fuec.vehiculo_marca || ""} {fuec.vehiculo?.modelo || fuec.vehiculo_modelo || ""}
          </Descriptions.Item>
          {(fuec.vehiculo?.clase || fuec.vehiculo_clase) && (
            <Descriptions.Item label="Clase">
              {fuec.vehiculo?.clase || fuec.vehiculo_clase}
            </Descriptions.Item>
          )}
          {(fuec.vehiculo?.capacidad || fuec.vehiculo_capacidad) && (
            <Descriptions.Item label="Capacidad">
              {fuec.vehiculo?.capacidad || fuec.vehiculo_capacidad} pasajeros
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* Conductor */}
      <Card
        title={<><UserOutlined style={{ marginRight: 8 }} />Conductor</>}
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Descriptions column={1} size="small">
          <Descriptions.Item label="Nombre">
            <Text strong>
              {fuec.conductor?.nombre || fuec.conductor?.nombres || fuec.conductor_nombre || "N/A"} {fuec.conductor?.apellidos || ""}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Documento">
            {fuec.conductor?.tipo_documento || "CC"} {fuec.conductor?.numero_documento || fuec.conductor?.cedula || fuec.conductor_cedula || "N/A"}
          </Descriptions.Item>
          {(fuec.conductor?.licencia || fuec.conductor_licencia) && (
            <Descriptions.Item label="Licencia">
              {fuec.conductor?.licencia || fuec.conductor_licencia}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* Empresa/Cliente */}
      {fuec.cliente && (
        <Card
          title={<><EnvironmentOutlined style={{ marginRight: 8 }} />Empresa</>}
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Nombre">
              <Text strong>{fuec.cliente.nombre_cliente || fuec.cliente.razon_social || fuec.cliente_nombre}</Text>
            </Descriptions.Item>
            {fuec.cliente.nit && (
              <Descriptions.Item label="NIT">
                {fuec.cliente.nit}
              </Descriptions.Item>
            )}
            {fuec.cliente.direccion && (
              <Descriptions.Item label="Dirección">
                {fuec.cliente.direccion}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      )}

      {/* Ruta/Trayecto */}
      {(fuec.origen_ciudad_nombre || fuec.destino_ciudad_nombre || fuec.origenCiudad || fuec.destinoCiudad) && (
        <Card
          title={<><EnvironmentOutlined style={{ marginRight: 8 }} />Trayecto</>}
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Origen">
              {fuec.origenCiudad?.nombre || fuec.origen_ciudad_nombre || "N/A"}, {fuec.origenDepartamento?.nombre || fuec.origen_departamento_nombre || ""}
            </Descriptions.Item>
            <Descriptions.Item label="Destino">
              {fuec.destinoCiudad?.nombre || fuec.destino_ciudad_nombre || "N/A"}, {fuec.destinoDepartamento?.nombre || fuec.destino_departamento_nombre || ""}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {/* Objeto del Contrato */}
      {(fuec.objetoContrato?.objeto_contrato || fuec.objeto_contrato_texto) && (
        <Card
          title={<><CheckCircleOutlined style={{ marginRight: 8 }} />Objeto del Contrato</>}
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Text>{fuec.objetoContrato?.objeto_contrato || fuec.objeto_contrato_texto}</Text>
        </Card>
      )}

      {/* Botón de descarga al final */}
      <div style={{ textAlign: "center", marginTop: 24 }}>
        <Button
          type="primary"
          size="large"
          icon={<DownloadOutlined />}
          onClick={handleDownloadPdf}
          loading={downloading}
          style={{ width: "100%", maxWidth: 300 }}
        >
          Descargar FUEC en PDF
        </Button>
      </div>
    </div>
  );
};

export default FuecDetail;
